import { useEffect, useState } from "react";
import { api, formatError } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { History, RotateCcw, Wand2, User, Zap, Undo2, AlertTriangle, TrendingDown } from "lucide-react";
import { toast } from "sonner";

const SOURCE_META = {
  manual: { label: "Manual edit", icon: User, color: "bg-neutral-500" },
  rewrite: { label: "Rewrite", icon: Wand2, color: "bg-pink" },
  compare: { label: "Compare pick", icon: Wand2, color: "bg-pink" },
  "auto-fix": { label: "Auto-fix", icon: Zap, color: "bg-pink" },
  revert: { label: "Revert", icon: Undo2, color: "bg-amber-500" },
};

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
  } catch { return iso.slice(0, 16); }
}

export default function RewriteHistory({ jobId, open, onOpenChange, onReverted }) {
  const [entries, setEntries] = useState([]);
  const [guardrail, setGuardrail] = useState(null);
  const [loading, setLoading] = useState(false);
  const [reverting, setReverting] = useState(null);
  const [expandedIdx, setExpandedIdx] = useState(null);

  const load = () => {
    if (!jobId) return;
    setLoading(true);
    Promise.all([
      api.get(`/recruit/jobs/${jobId}/rewrite-history`),
      api.get(`/recruit/jobs/${jobId}/rewrite-guardrail`).catch(() => ({ data: { alerts: [], worst_severity: null } })),
    ])
      .then(([h, g]) => { setEntries(h.data); setGuardrail(g.data); })
      .catch(() => { setEntries([]); setGuardrail(null); })
      .finally(() => setLoading(false));
  };

  useEffect(() => { if (open) load(); }, [open, jobId]);

  const revert = async (idx) => {
    if (!confirm("Revert the job description to this earlier version?")) return;
    setReverting(idx);
    try {
      await api.post(`/recruit/jobs/${jobId}/rewrite-history/${idx}/revert`);
      toast.success("Description reverted");
      load();
      onReverted && onReverted();
    } catch (e) { toast.error(formatError(e.response?.data?.detail) || "Revert failed"); }
    finally { setReverting(null); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="rewrite-history-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <History className="w-4 h-4 text-pink" /> Description history
          </DialogTitle>
          <DialogDescription className="text-xs text-neutral-500">
            Every AI rewrite and manual edit is logged here. Revert any change if the new copy hurts conversion.
          </DialogDescription>
        </DialogHeader>

        {loading && <div className="py-8 text-center text-sm text-neutral-500">Loading…</div>}

        {guardrail?.alerts?.length > 0 && (
          <div className={`rounded-lg border-l-4 p-3 mb-3 ${guardrail.worst_severity === "critical" ? "border-l-red-500 bg-red-50" : "border-l-amber-500 bg-amber-50"}`} data-testid="guardrail-card">
            <div className="flex items-center gap-2 mb-1">
              <AlertTriangle className={`w-4 h-4 ${guardrail.worst_severity === "critical" ? "text-red-500" : "text-amber-600"}`} />
              <span className="font-semibold text-sm">A/B guardrail — this JD's rewrites may be hurting quality</span>
            </div>
            <div className="space-y-2 mt-2">
              {guardrail.alerts.map(a => (
                <div key={a.entry_idx} className="flex items-center justify-between gap-3 flex-wrap bg-white/60 rounded p-2 text-xs" data-testid={`guardrail-alert-${a.entry_idx}`}>
                  <div className="min-w-0 flex items-center gap-2">
                    <TrendingDown className="w-3.5 h-3.5 text-red-500" />
                    <span className="truncate"><span className="font-semibold capitalize">{a.source}</span>{a.mode ? ` · ${a.mode}` : ""} · {a.message}</span>
                  </div>
                  <Button size="sm" variant="outline" onClick={() => revert(a.entry_idx)} disabled={reverting === a.entry_idx}
                    className="shrink-0 text-xs h-7" data-testid={`guardrail-revert-${a.entry_idx}`}>
                    <RotateCcw className="w-3 h-3 mr-1" /> {reverting === a.entry_idx ? "Reverting…" : "Revert this"}
                  </Button>
                </div>
              ))}
            </div>
          </div>
        )}

        {!loading && entries.length === 0 && (
          <div className="py-8 text-center text-sm text-neutral-500">
            No history yet. Any AI rewrite or manual description edit will show up here.
          </div>
        )}

        <div className="space-y-3">
          {entries.map(e => {
            const meta = SOURCE_META[e.source] || SOURCE_META.manual;
            const Icon = meta.icon;
            const isOpen = expandedIdx === e.idx;
            const delta = (e.after_length || 0) - (e.before_length || 0);
            return (
              <div key={e.idx} className="rounded-lg border" data-testid={`hist-row-${e.idx}`}>
                <div className="px-4 py-3 flex items-start justify-between gap-3 flex-wrap">
                  <div className="flex items-start gap-3 min-w-0">
                    <div className={`w-8 h-8 rounded-full ${meta.color} text-white grid place-items-center shrink-0`}>
                      <Icon className="w-4 h-4" />
                    </div>
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-semibold text-sm">{meta.label}</span>
                        {e.mode && <Badge variant="outline" className="text-[10px] capitalize">{e.mode}</Badge>}
                        {e.seniority && <Badge variant="outline" className="text-[10px]">{e.seniority}</Badge>}
                        <span className="text-xs text-neutral-500">· {fmtDate(e.applied_at)}</span>
                      </div>
                      <div className="text-xs text-neutral-500 mt-0.5">
                        by {e.applied_by_name || "—"} · {e.before_length}→{e.after_length} chars ({delta > 0 ? "+" : ""}{delta})
                      </div>
                      {e.rationale && (
                        <div className="text-xs text-neutral-600 italic mt-1 line-clamp-2">"{e.rationale}"</div>
                      )}
                    </div>
                  </div>
                  <div className="flex gap-2 shrink-0">
                    <Button size="sm" variant="ghost" onClick={() => setExpandedIdx(isOpen ? null : e.idx)} data-testid={`hist-view-${e.idx}`}>
                      {isOpen ? "Hide diff" : "View diff"}
                    </Button>
                    {e.source !== "revert" && (
                      <Button size="sm" variant="outline" onClick={() => revert(e.idx)} disabled={reverting === e.idx}
                        data-testid={`hist-revert-${e.idx}`}>
                        <RotateCcw className="w-3.5 h-3.5 mr-1" /> {reverting === e.idx ? "Reverting…" : "Revert to before"}
                      </Button>
                    )}
                  </div>
                </div>

                {isOpen && (
                  <div className="border-t px-4 py-3 grid md:grid-cols-2 gap-3 bg-neutral-50/50">
                    <div>
                      <div className="text-xs font-medium text-neutral-500 mb-1">Before</div>
                      <div className="text-xs whitespace-pre-wrap max-h-[220px] overflow-y-auto bg-white p-2 rounded border">
                        {e.before || <span className="text-neutral-400 italic">— empty —</span>}
                      </div>
                    </div>
                    <div>
                      <div className="text-xs font-medium text-neutral-500 mb-1">After</div>
                      <div className="text-xs whitespace-pre-wrap max-h-[220px] overflow-y-auto bg-white p-2 rounded border">
                        {e.after || <span className="text-neutral-400 italic">— empty —</span>}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </DialogContent>
    </Dialog>
  );
}
